import React, { useEffect, useState, useRef, useCallback } from "react";
import { api, fmtNum, fmtKg, errMsg } from "@/lib/api";
import { EmptyState } from "@/components/app/EmptyState";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Upload, Loader2, CloudDownload, FileText, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const fmtZeit = (iso) => {
  if (!iso) return "–";
  const d = new Date(iso);
  return d.toLocaleString("de-AT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function AdminImportPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [ftpLoading, setFtpLoading] = useState(false);
  const fileRef = useRef();

  const load = useCallback(() => {
    api.get("/import/protokoll").then((res) => setLogs(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/import/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const log = res.data;
      if (log.status === "erfolgreich") {
        toast.success(`Import erfolgreich: ${log.neu_importiert} neue Lieferungen (${fmtNum(log.summe_kg)} kg), ${log.duplikate} Duplikate übersprungen.`);
      } else {
        toast.error(`Import fehlgeschlagen: ${log.fehler?.join("; ")}`);
      }
      load();
    } catch (err) {
      toast.error(errMsg(err, "Upload fehlgeschlagen."));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const ftpAbholung = async () => {
    setFtpLoading(true);
    try {
      const res = await api.post("/import/ftp-abholung");
      toast.success(`FTP-Abholung abgeschlossen: ${res.data.abgeholt} Datei(en) verarbeitet.`);
      load();
    } catch (err) {
      toast.error(errMsg(err, "FTP-Abholung fehlgeschlagen."));
    } finally {
      setFtpLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">ÖNORM-Import</h1>
        <p className="text-sm text-muted-foreground">Datenträger der Milchsammelwagen importieren – per Datei-Upload oder FTP-Abholung vom Sammelwagenbetreiber.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Upload className="h-4 w-4" /> Datei-Upload</CardTitle>
            <CardDescription>ÖNORM-Datenträger (.dat/.txt) manuell hochladen.</CardDescription>
          </CardHeader>
          <CardContent>
            <input ref={fileRef} data-testid="onorm-file-input" type="file" accept=".dat,.txt,.csv" onChange={upload}
              className="block w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-primary file:px-4 file:py-2 file:text-primary-foreground file:text-sm file:font-medium hover:file:bg-primary/90 file:cursor-pointer" />
            {uploading && <p className="mt-2 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Datei wird verarbeitet …</p>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><CloudDownload className="h-4 w-4" /> FTP-Abholung</CardTitle>
            <CardDescription>Neue Datenträger automatisch vom Portal des Sammelwagenbetreibers abholen.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button data-testid="ftp-fetch-start-button" onClick={ftpAbholung} disabled={ftpLoading}>
              {ftpLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CloudDownload className="h-4 w-4 mr-2" />}
              Abholung starten
            </Button>
            <p className="mt-2 text-xs text-muted-foreground">FTP-Zugangsdaten unter Schnittstellen → FTP-Einstellungen konfigurieren.</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Import-Protokoll</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : logs.length ? (
            <div className="overflow-x-auto">
              <Table data-testid="import-log-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Zeitpunkt</TableHead>
                    <TableHead>Datei</TableHead>
                    <TableHead>Quelle</TableHead>
                    <TableHead className="text-right">Neu</TableHead>
                    <TableHead className="text-right">Duplikate</TableHead>
                    <TableHead className="text-right">Summe</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Hinweise</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell className="text-sm whitespace-nowrap">{fmtZeit(l.zeitpunkt)}</TableCell>
                      <TableCell className="font-medium text-sm"><span className="flex items-center gap-1.5"><FileText className="h-3.5 w-3.5 text-muted-foreground" />{l.dateiname}</span></TableCell>
                      <TableCell><Badge variant="outline">{l.quelle}</Badge></TableCell>
                      <TableCell className="text-right font-num">{fmtNum(l.neu_importiert)}</TableCell>
                      <TableCell className="text-right font-num">{fmtNum(l.duplikate)}</TableCell>
                      <TableCell className="text-right font-num">{fmtKg(l.summe_kg)}</TableCell>
                      <TableCell><Badge variant={l.status === "erfolgreich" ? "secondary" : "destructive"}>{l.status}</Badge></TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[280px]">
                        {l.fehler?.length > 0 && <span className="text-destructive flex items-center gap-1"><AlertCircle className="h-3 w-3 shrink-0" />{l.fehler.join("; ")}</span>}
                        {l.warnungen?.length > 0 && <span className="block truncate" title={l.warnungen.join("; ")}>{l.warnungen.length} Warnung(en)</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <EmptyState icon={Upload} title="Noch keine Importe durchgeführt." text="Laden Sie einen ÖNORM-Datenträger hoch oder starten Sie die FTP-Abholung." />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
